import { useCallback, useMemo } from 'react';

import { useApi } from '../useApi/useApi.hook';

import type {
  FetchFunctionReturnType,
  UseMutationParams,
  UseMutationResult,
} from './useMutation.types';

export function useMutation<ResponseType, RequestType>({
  url,
  method,
  headers,
  baseUrl,
  shouldTrackException,
  searchParams,
  keepalive,
  acceptNoContent,
}: UseMutationParams): UseMutationResult<ResponseType, RequestType> {
  const urlWithParams = useMemo(() => {
    if (!searchParams) {
      return url;
    }

    return `${url}?${searchParams.toString()}`;
  }, [searchParams, url]);

  const { error, isLoading, status, executeRequest } = useApi<ResponseType>(
    urlWithParams,
    {
      method,
      headers,
      ...(keepalive && { keepalive }),
    },
    baseUrl,
    shouldTrackException
  );

  const execute = useCallback(
    async (
      params?: RequestType
    ): Promise<FetchFunctionReturnType<ResponseType>> => {
      try {
        const result = await executeRequest(
          params ? { body: JSON.stringify(params) } : void 0
        );

        if (result === null && !acceptNoContent) {
          return {
            result: null,
            error: new Error('Fetch returns null'),
          };
        }

        return {
          result,
          error: null,
        };
      } catch (error) {
        return {
          result: null,
          error,
        };
      }
    },
    [executeRequest, acceptNoContent]
  );

  return [
    execute,
    {
      isError: Boolean(error),
      isLoading,
      isFetching: isLoading,
      status,
      error: error ?? null,
    },
  ] as const;
}
